import { useState } from "react";
import { motion } from "framer-motion";
import { 
  ArrowRight, 
  Shield, 
  CheckCircle,
  XCircle,
  AlertTriangle,
  FileText,
  Send
} from "lucide-react";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { SEOHead } from "@/components/SEOHead";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const nonNegotiableRequirements = [
  "Existing professional service business or consulting practice",
  "Minimum 2 years operating history",
  "Demonstrated client delivery track record",
  "Willingness to submit to audit and enforcement",
  "Understanding that certification does not guarantee license",
];

const notAccepted = [
  "Startups without established operations",
  "Individuals seeking coaching or mentorship",
  "Businesses unwilling to document processes",
  "Those expecting immediate certification without review",
];

const applicationProcess = [
  { step: "01", title: "Application Submitted", desc: "Initial review within 5 business days" },
  { step: "02", title: "Preliminary Assessment", desc: "Readiness diagnostic issued if qualified" },
  { step: "03", title: "Acceptance Decision", desc: "Approved, Conditional, or Declined" },
  { step: "04", title: "Certification Access", desc: "Portal access granted upon acceptance" },
];

const OperatorApplicationPage = () => {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    company: "",
    yearsOperating: "",
    primaryService: "",
    teamSize: "",
    currentRevenue: "",
    whyApplying: "",
    acceptsTerms: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.acceptsTerms) {
      toast.error("You must acknowledge the application terms");
      return;
    }

    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${API_URL}/api/operator-application`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setSubmitted(true);
        toast.success("Application submitted successfully");
      } else {
        toast.error("Failed to submit application. Please try again.");
      }
    } catch (error) {
      console.error("Application error:", error);
      toast.error("An error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#080808]">
        <Navbar />
        <section className="pt-32 pb-24 md:pt-44 px-6 md:px-12">
          <div className="max-w-2xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <CheckCircle className="h-16 w-16 text-primary mx-auto mb-6" />
              <h1 className="font-serif text-4xl md:text-5xl font-light text-foreground mb-6">
                Application<br />
                <span className="text-primary">Received</span>
              </h1>
              <p className="text-muted-foreground mb-8">
                Your application has been submitted for review. You will receive 
                a response within 5 business days at the email address provided.
              </p>
              <div className="bg-card/5 border border-white/10 p-6 text-left">
                <p className="text-xs text-muted-foreground uppercase mb-3">What Happens Next:</p>
                <ul className="space-y-2 text-sm text-foreground">
                  <li>• Initial review of your application</li>
                  <li>• Preliminary assessment (if qualified)</li>
                  <li>• Final decision: Approved, Conditional, or Declined</li>
                </ul>
              </div>
            </motion.div>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#080808]" data-testid="operator-application-page">
      <SEOHead 
        title="Operator Application - OnPoint Authority Systems"
        description="Apply for operator certification. Acceptance is selective. This is not open enrollment."
        url="https://onpointauthority.com/operator-application"
      />
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-16 md:pt-44 md:pb-24 px-6 md:px-12" data-testid="application-hero">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-6">
              Selective Access
            </p>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-light tracking-tight leading-[0.95] text-foreground mb-8">
              Operator<br />
              <span className="text-primary">Application</span>
            </h1>
            <p className="text-lg md:text-xl font-light leading-relaxed text-muted-foreground max-w-2xl">
              This is not enrollment. This is not a purchase form. 
              This is an application for consideration. 
              Acceptance is selective.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Requirements */}
      <section className="py-16 md:py-24 px-6 md:px-12 border-t border-white/5" data-testid="requirements-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Non-Negotiable */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="flex items-center gap-3 mb-6">
                <Shield className="h-6 w-6 text-primary" />
                <h2 className="font-serif text-2xl font-light text-foreground">Non-Negotiable Requirements</h2>
              </div>
              <ul className="space-y-4">
                {nonNegotiableRequirements.map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-foreground">
                    <CheckCircle className="h-4 w-4 text-primary flex-shrink-0 mt-1" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Not Accepted */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="flex items-center gap-3 mb-6">
                <XCircle className="h-6 w-6 text-red-400" />
                <h2 className="font-serif text-2xl font-light text-foreground">Applications Not Accepted</h2>
              </div>
              <ul className="space-y-4">
                {notAccepted.map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-muted-foreground">
                    <div className="w-1.5 h-1.5 bg-red-400 rounded-full mt-2.5" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-16 md:py-24 px-6 md:px-12 bg-[#050505]" data-testid="process-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-12"
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-4">
              What to Expect
            </p>
            <h2 className="font-serif text-3xl font-light text-foreground">
              Application <span className="text-primary">Process</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {applicationProcess.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="bg-card/5 border border-white/10 p-5"
              >
                <p className="text-xs font-mono text-primary mb-2">{item.step}</p>
                <h3 className="text-sm font-medium text-foreground mb-2">{item.title}</h3>
                <p className="text-xs text-muted-foreground">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-16 md:py-24 px-6 md:px-12 border-t border-white/5" data-testid="application-form">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-12 text-center"
          >
            <FileText className="h-10 w-10 text-primary mx-auto mb-4" />
            <h2 className="font-serif text-3xl font-light text-foreground mb-4">
              Submit <span className="text-primary">Application</span>
            </h2>
            <p className="text-muted-foreground">
              Complete all fields. Incomplete applications will not be reviewed.
            </p>
          </motion.div>

          <motion.form
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            onSubmit={handleSubmit}
            className="space-y-6"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-xs text-muted-foreground uppercase tracking-wider mb-2">
                  Full Name *
                </label>
                <Input
                  type="text"
                  required
                  value={formData.fullName}
                  onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                  className="bg-card/5 border-white/10 rounded-none"
                  data-testid="input-full-name"
                />
              </div>
              <div>
                <label className="block text-xs text-muted-foreground uppercase tracking-wider mb-2">
                  Email *
                </label>
                <Input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="bg-card/5 border-white/10 rounded-none"
                  data-testid="input-email"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-xs text-muted-foreground uppercase tracking-wider mb-2">
                  Company / Business Name *
                </label>
                <Input
                  type="text"
                  required
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  className="bg-card/5 border-white/10 rounded-none"
                  data-testid="input-company"
                />
              </div>
              <div>
                <label className="block text-xs text-muted-foreground uppercase tracking-wider mb-2">
                  Years Operating *
                </label>
                <select
                  required
                  value={formData.yearsOperating}
                  onChange={(e) => setFormData({ ...formData, yearsOperating: e.target.value })}
                  className="w-full h-10 px-3 bg-card/5 border border-white/10 text-foreground rounded-none"
                  data-testid="select-years"
                >
                  <option value="">Select</option>
                  <option value="2-3">2-3 years</option>
                  <option value="4-6">4-6 years</option>
                  <option value="7-10">7-10 years</option>
                  <option value="10+">10+ years</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-xs text-muted-foreground uppercase tracking-wider mb-2">
                  Primary Service Offering *
                </label>
                <Input
                  type="text"
                  required
                  value={formData.primaryService}
                  onChange={(e) => setFormData({ ...formData, primaryService: e.target.value })}
                  className="bg-card/5 border-white/10 rounded-none"
                  placeholder="e.g., Management Consulting, Advisory"
                  data-testid="input-service"
                />
              </div>
              <div>
                <label className="block text-xs text-muted-foreground uppercase tracking-wider mb-2">
                  Team Size *
                </label>
                <select
                  required
                  value={formData.teamSize}
                  onChange={(e) => setFormData({ ...formData, teamSize: e.target.value })}
                  className="w-full h-10 px-3 bg-card/5 border border-white/10 text-foreground rounded-none"
                  data-testid="select-team-size"
                >
                  <option value="">Select</option>
                  <option value="1">Solo operator</option>
                  <option value="2-5">2-5</option>
                  <option value="6-10">6-10</option>
                  <option value="11-25">11-25</option>
                  <option value="25+">25+</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-xs text-muted-foreground uppercase tracking-wider mb-2">
                Current Annual Revenue Range *
              </label>
              <select
                required
                value={formData.currentRevenue}
                onChange={(e) => setFormData({ ...formData, currentRevenue: e.target.value })}
                className="w-full h-10 px-3 bg-card/5 border border-white/10 text-foreground rounded-none"
                data-testid="select-revenue"
              >
                <option value="">Select</option>
                <option value="under-250k">Under $250K</option>
                <option value="250k-500k">$250K - $500K</option>
                <option value="500k-1m">$500K - $1M</option>
                <option value="1m-5m">$1M - $5M</option>
                <option value="5m+">$5M+</option>
              </select>
            </div>

            <div>
              <label className="block text-xs text-muted-foreground uppercase tracking-wider mb-2">
                Why are you applying? *
              </label>
              <Textarea
                required
                value={formData.whyApplying}
                onChange={(e) => setFormData({ ...formData, whyApplying: e.target.value })}
                className="bg-card/5 border-white/10 rounded-none min-h-[120px]"
                placeholder="Describe your interest in authority certification and how you expect to apply it to your business."
                data-testid="textarea-why"
              />
            </div>

            {/* Disclaimer */}
            <div className="bg-yellow-500/10 border border-yellow-500/20 p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-yellow-400 flex-shrink-0" />
                <div className="text-sm text-foreground">
                  <p className="font-medium mb-2">Application Terms</p>
                  <ul className="text-xs text-muted-foreground space-y-1">
                    <li>• Acceptance is selective and not guaranteed</li>
                    <li>• Submitting an application does not constitute enrollment</li>
                    <li>• All information provided is subject to verification</li>
                    <li>• Decision is final and at sole discretion of OnPoint Authority Systems</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="acceptTerms"
                checked={formData.acceptsTerms}
                onChange={(e) => setFormData({ ...formData, acceptsTerms: e.target.checked })}
                className="mt-1"
                data-testid="checkbox-terms"
              />
              <label htmlFor="acceptTerms" className="text-sm text-muted-foreground">
                I acknowledge that this is an application for consideration, not a purchase or enrollment. 
                I understand acceptance is selective.
              </label>
            </div>

            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-[0.15em] text-xs font-bold py-6 rounded-none"
              data-testid="submit-application-btn"
            >
              {isSubmitting ? (
                "Submitting..."
              ) : (
                <>
                  Submit Application
                  <Send className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          </motion.form>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default OperatorApplicationPage;
